import { Location } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { ActivatedRoute } from '@angular/router';
import { Subscription, merge } from 'rxjs';
import { Gruppe } from '../../classes/gruppe';

import { EinstallenService } from '../einstallen.service';

@Component({
    selector: 'kiene-einstallen-uebersicht',
    templateUrl: './einstallen-uebersicht.component.html',
    styleUrls: ['./einstallen-uebersicht.component.scss', './einstallen-uebersicht.component.fixed.scss'],
})
export class EinstallenUebersichtComponent implements OnInit, OnDestroy {
    @Input() gruppe: Gruppe;
    @Input() showPassReiter = false;
    /** Standalone mobile route: always start a fresh hidden Einstall-Tierliste. */
    @Input() forceNewEinstallTierliste = false;

    currentTabIndex = 0;
    /** Kundenportal phone route: scan + number pad tabs. */
    showMobileTabs = false;
    showMobileReviewStep = false;
    tierlisteAnzahl = 0;

    private tierlisteAnzahlSubscription?: Subscription;

    constructor(
        private einstallService: EinstallenService,
        private readonly route: ActivatedRoute,
        private readonly location: Location,
    ) {}

    goBack(): void {
        this.location.back();
    }

    ngOnInit(): void {
        if (this.route.snapshot.paramMap.get('gruppeid')) {
            this.forceNewEinstallTierliste = true;
            this.showMobileTabs = true;
        }
        if (this.forceNewEinstallTierliste) {
            this.einstallService.clearCurrentEinstallTierlisteId();
            this.einstallService.clearLastScanErgebnisse();
            this.einstallService.clearPendingGewichte();
            this.einstallService.clearCurrentEinstalldatum();
            this.einstallService.clearCurrentHerkunft();
        }
        this.einstallService.watchCurrentGruppe().subscribe({
            next: gruppe => {
                this.gruppe = gruppe;
                if (this.showMobileTabs && gruppe?.gruppe_id) {
                    this.einstallService
                        .ensureEinstallTierliste(gruppe.gruppe_id, gruppe.kunde_id, this.forceNewEinstallTierliste)
                        .subscribe({
                            next: () => this.refreshTierlisteAnzahl(),
                        });
                }
                this.refreshTierlisteAnzahl();
            },
        });

        if (this.showMobileTabs) {
            this.tierlisteAnzahlSubscription = merge(
                this.einstallService.watchCurrentEinstallTierlisteId(),
                this.einstallService.watchLastScanErgebnisse(),
                this.einstallService.watchScanErgebnisSubject(),
            ).subscribe({
                next: () => this.refreshTierlisteAnzahl(),
            });
        }
    }

    ngOnDestroy(): void {
        this.tierlisteAnzahlSubscription?.unsubscribe();
    }

    tabChanged(event: MatTabChangeEvent): void {
        this.currentTabIndex = event.index;
    }

    isMobileListeAbschliessenDisabled(): boolean {
        return this.tierlisteAnzahl <= 0;
    }

    openMobileReview(): void {
        if (this.isMobileListeAbschliessenDisabled()) {
            return;
        }
        this.showMobileReviewStep = true;
    }

    closeMobileReview(): void {
        this.showMobileReviewStep = false;
        this.refreshTierlisteAnzahl();
    }

    onMobileReviewCompleted(): void {
        this.einstallService.clearLastScanErgebnisse();
        this.showMobileReviewStep = false;
        this.location.back();
    }

    private refreshTierlisteAnzahl(): void {
        const gruppeId = Number(this.gruppe?.gruppe_id || 0);
        const tierlisteId = Number(this.einstallService.getCurrentEinstallTierlisteId() || 0);
        if (!gruppeId || !tierlisteId) {
            this.tierlisteAnzahl = 0;
            return;
        }
        this.einstallService.loadEinstallTierlisteAnzahl(gruppeId, tierlisteId, null).subscribe({
            next: anzahl => {
                this.tierlisteAnzahl = anzahl;
            },
            error: () => {
                this.tierlisteAnzahl = 0;
            },
        });
    }
}
